<template>
    <div id="myAccount" style="width: 100%;">
      <patternlib-toast position="top-right" ref="infoToast"/>
      <div id="content" v-show="!loading">

        <patternlib-headline :headline="$t('myAccount.headline')" heading-tag="h2" separator="none" style="margin-bottom: 64px;"></patternlib-headline>

        <patternlib-headline :headline="$t('myAccount.credentials')" heading-tag="h3" separator="none" style="margin-bottom: var(--spacing-6);"></patternlib-headline>
        <patternlib-textinput 
          :label="$t('myAccount.eMailAddress')"
          style="width: calc(50% - var(--spacing-4));"
          display
          id="eMail"
          :value="eMailAddress"
        >
        </patternlib-textinput>
        <div style="display: flex; flex-direction: row; margin-bottom: var(--spacing-7);">
          <patternlib-icon icon-name="info-circle"/>
          <span v-html="$t('myAccount.eMailAddressInfo', {'URL': getCustomerServiceUrl()})"></span>
        </div>

        <div>
          <patternlib-headline :headline="$t('myAccount.changePassword')" heading-tag="h3" separator="none"></patternlib-headline>

          <div id="password">
            <div style="width: 100%;">
              <patternlib-textinput 
                :label="$t('myAccount.currentPassword')" 
                type="password" 
                style="display: block; width: 100%; margin-bottom: var(--spacing-4); margin-top: var(--spacing-4);" 
                ref="currentPassword"
                :error-text="$t('myAccount.currentPasswordWrong')"
                :status="currentPasswordStatus"
              />
              <newPassword ref="newPassword"/>
              <patternlib-alert-message icon-name="info-circle" type="error" v-if="failedPasswordChange">{{ $t("myAccount.failedPasswordChange") }}</patternlib-alert-message>
              <patternlib-button 
                :label="$t('myAccount.saveNewPassword')" 
                style="margin-top: var(--spacing-5);"
                size="small" 
                @click="changePassword"
                :loading="savingPassword"
              />
            </div>

            <passwordTip id="tipBox"/>

          </div>

          <div
            style="
              width: 100%;
              height: 0px;
              border-bottom-width: 1px;
              border-bottom-color: var(--color-steel-300);
              margin-top: 64px;
              margin-bottom: 64px;
            "
          ></div>

          <personalData 
            ref="personalData"
            @personalData:firstname = "(newValue) => {$refs.newPassword.firstname = newValue}"
            @personalData:lastname = "(newValue) => {$refs.newPassword.lastname = newValue}"
          />
          <patternlib-button 
            :label="$t('myAccount.saveCustomerData')" 
            style="margin-top: 10px; margin-bottom: var(--spacing-6);" 
            @click="saveUserData" 
            :loading="savingData"
          />
          
        </div>
      
        <div style="
          width: 100%;
          padding: var(--spacing-6) var(--spacing-6);
          background-color: var(--color-steel-100);
          margin-top: var(--spacing-xxsxxxl);
          position: relative;
        ">
          <patternlib-headline :headline="$t('deleteAccount.headline')" heading-tag="h3" separator="none"></patternlib-headline>
          <p style="margin-top: var(--spacing-4);">{{ $t("deleteAccount.text") }}</p>

          <div>
            <patternlib-button 
              :label="$t('deleteAccount.delete')"
              size="small"
              icon-position="right"
              style="margin-right: var(--spacing-4);  margin-top: var(--spacing-5);"
              @click="showDeleteAccountModal = true"
            >
              <patternlib-icon icon-name="font-arrow" size="24px" slot="icon"></patternlib-icon>
            </patternlib-button>

            <patternlib-button
              :label="$t('deleteAccount.privacyPolicy')"
              size="small"
              type="secondary"
              icon-position="right"
              style="margin-top: var(--spacing-5);"
              @click="showPrivacyPolicy"
            >
              <patternlib-icon icon-name="font-arrow" size="24px" slot="icon"></patternlib-icon>
            </patternlib-button>
          </div>

          <div
          style="
            background-color: var(--color-artic-blue);
            position: absolute;
            right: 0px;
            top: -30px;
            width: 72px;
            height: 72px;
            display: flex;
            align-items: center;
            justify-content: center;
          "
          >
            <patternlib-icon icon-name="info-circle" size="40px" style="color: var(--color-white);"></patternlib-icon>
          </div>
        </div>

        <patternlib-modal 
          :show="showDeleteAccountModal"
          :modal-title="$t('deleteAccount.modal.title')"
          :cancel-label="$t('modalAppliance.cancel')"
          cancel-type="secondary"
          :confirm-label="$t('deleteAccount.delete')"
          @lhCancel="showDeleteAccountModal = false"
          @lhClose="showDeleteAccountModal = false"
          @lhConfirm="deleteAccount()"
        >
          <span slot="modal-content">{{ $t("deleteAccount.modal.text") }}</span>
        </patternlib-modal>

      </div>
      <patternlib-spinner spin v-if="loading"/>
    </div>
</template>

<script>
import { PatternlibToast } from "@liebherr/patternlib-vue";
import passwordTip from "../components/passwordTip.vue"
import personalData from "../components/personalData.vue"
import newPassword from "../components/newPassword.vue"
import { useServices } from "../services/useServices";
import { useGlobal } from "../stores/globalStore"

export default {
  data: () => ({
    eMailAddress: "",
    savingData: false,
    savingPassword: false,
    loading: true,
    currentPasswordStatus: "default",
    failedPasswordChange: false,
    toastIdCounter: 0,
    showDeleteAccountModal: false
  }),

  components: {
    passwordTip,
    personalData,
    newPassword
  },

  computed: {
  },

  methods: {
    saveUserData() {
      this.savingData = true
      let userData = this.$refs.personalData.getData()

      const services = useServices()
      services.$api.updateUserData((successful) => {
        this.savingData = false
        if(successful){
          this.$refs.infoToast.createToastItem({
            "type": "success",
            "itemId": this.getToastId(),
            "showIcon": true,
            "autoHide": true,
            "delay": 10000,
            "message": this.$t("myAccount.customerDataSaveSuccessful"),
          })
        }
      }, userData)

      const globalStore = useGlobal()
      globalStore.userData = {
        email: this.eMailAddress,
        ...userData
      }
    },

    changePassword() {
      this.failedPasswordChange = false
      let data = this.$refs.personalData.getData()
      let newPassword = this.$refs.newPassword.getPassword(data.firstname, data.lastname, data.email)
      if (!newPassword) {
        return
      }
      this.currentPasswordStatus = "default"
      this.savingPassword = true

      const services = useServices()
      services.$api.changePassword((response) => {
        if(!response.successful) {
          this.savingPassword = false
          if(response.error == "Authentication") {
            this.currentPasswordStatus = "error"
          }
          else {
            this.failedPasswordChange = true
          }
          return
        }
        this.$refs.infoToast.createToastItem({
          "type": "success",
          "itemId": this.getToastId(),
          "showIcon": true,
          "autoHide": true,
          "delay": 10000,
          "message": this.$t("myAccount.passwordChangeSuccessful"),
        })
        this.$refs.currentPassword.value = ""
        this.$refs.newPassword.clearFields()
        this.savingPassword = false
      }, this.$refs.currentPassword.value, newPassword)
    },

    getToastId() {
      this.toastIdCounter+=1
      return this.toastIdCounter.toString()
    },

    deleteAccount() {
      this.showDeleteAccountModal = false
      this.loading = true
      const services = useServices()
      services.$api.deleteAccount((successful) => {
        if(successful) {
          this.$router.push("/logout")
          return
        }
        this.loading = false
        this.$emit("showMessage", {
            type: "alert",
            showIcon: true,
            message: this.$t("unknownError")
        })
      } )
    },

    setData(data) {
      this.eMailAddress = data.email
      this.$refs.personalData.gender = data.gender
      this.$refs.personalData.title = data.title
      this.$refs.personalData.firstname = data.firstname
      this.$refs.personalData.lastname = data.lastname
      this.$refs.personalData.street = data.streetAndNumber
      this.$refs.personalData.postalCode = data.postalCode
      this.$refs.personalData.city = data.city
      this.$refs.personalData.currentCountryId = data.countryId
      this.$refs.personalData.phoneNumber = data.phonenumber

      this.$refs.newPassword.firstname = data.firstname
      this.$refs.newPassword.lastname = data.lastname
      this.$refs.newPassword.email = data.email
    },

    showPrivacyPolicy() {
      if (!window.lh) {
        this.$emit("showMessage", {
          type: "alert",
          showIcon: true,
          message: "Please change to a DWL-Page where MyLiebherr is implemented! PrivacyPolicy can only be displayed on DWL-Pages!"
        })
      }
        window.lh.openPageModalv2("hau-modal-myliebherr-privacy")
    },

    getCustomerServiceUrl() {
      const globalStore = useGlobal()
      return globalStore.customerServiceUrl
    }
  },

  mounted() {
    const services = useServices()
    const globalStore = useGlobal()

    if (globalStore.userData != null) {
      this.setData(globalStore.userData)
      this.loading = false
    }

    services.$api.getUserData((data) => {
      this.loading = false

      this.setData(data)
      this.$refs.personalData.displayOnly = false
      globalStore.userData = data
    })
  },
};
</script>

<style lang="scss" scoped>
#myAccount {
    display: flex;
    flex-direction: column;
    align-items: center;
}

#content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 90%;
}

#content > div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

#password {
  display: flex;
  flex-direction: row;
  //align-items: center;
  width: 100%;
  gap: var(--spacing-6);
}

#tipBox{
  margin-top: 64px;
}

@media only screen and (max-width: 770px) { 
  #password {
    flex-direction: column !important;
  }

  patternlib-button {
    display: block !important;
    width: 100%;
  }

  #eMail, #land, #postalCode, #salutation {
    width: 100% !important;
  }

  #tipBox{
    margin-top: var(--spacing-6);
  }
  
}

</style>